import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GroceriesPage from './GroceriesPage';
import {
  fetchSharedGroceryList,
  getUserCountry,
  updateSharedGroceryState,
} from '../../services/apiService';

const SharedGroceriesPage = () => {
  const { shareToken } = useParams();
  const { t } = useTranslation('mealplan');
  const [sharedData, setSharedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const latestUpdateIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const loadSharedList = async () => {
      setLoading(true);
      setError('');

      try {
        const [data, country] = await Promise.all([
          fetchSharedGroceryList(shareToken),
          getUserCountry().catch(() => 'Spain'),
        ]);

        if (cancelled) {
          return;
        }

        setSharedData({
          ...data,
          country: country || 'Spain',
          shoppingModeState: data?.shoppingModeState || {},
        });
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError?.status === 404
              ? t('grocery.sharedUnavailable', { defaultValue: 'This shared grocery list is no longer available.' })
              : t('grocery.sharedLoadError', { defaultValue: 'Unable to load this shared grocery list right now.' })
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSharedList();

    return () => {
      cancelled = true;
    };
  }, [shareToken, t]);

  const handleCheckedItemsChange = useCallback(
    async (nextState) => {
      const previousState = sharedData?.shoppingModeState || {};
      const updateId = latestUpdateIdRef.current + 1;
      latestUpdateIdRef.current = updateId;

      setSharedData((prev) => (prev ? { ...prev, shoppingModeState: nextState } : prev));

      try {
        const result = await updateSharedGroceryState(shareToken, nextState);
        if (latestUpdateIdRef.current === updateId) {
          setSharedData((prev) => (
            prev
              ? {
                  ...prev,
                  shoppingModeState: result?.shoppingModeState || nextState,
                }
              : prev
          ));
        }
      } catch (updateError) {
        if (latestUpdateIdRef.current === updateId) {
          setSharedData((prev) => (
            prev
              ? {
                  ...prev,
                  shoppingModeState: previousState,
                }
              : prev
          ));
          setError(
            t('grocery.sharedSaveError', { defaultValue: 'We could not save the latest checklist change. Please try again.' })
          );
        }
      }
    },
    [shareToken, sharedData?.shoppingModeState, t]
  );

  if (loading) {
    return (
      <div className="min-h-safe-screen bg-[#F8F8F7] px-6 py-10">
        <div className="max-w-5xl mx-auto text-[15px] text-[#65635D]">
          {t('grocery.sharedLoading', { defaultValue: 'Loading shared grocery list...' })}
        </div>
      </div>
    );
  }

  if (error && !sharedData) {
    return (
      <div className="min-h-safe-screen bg-[#F8F8F7] px-6 py-10">
        <div className="max-w-5xl mx-auto rounded-[12px] border-[0.5px] border-[#D7D7D5] bg-white px-5 py-4 text-[15px] text-[#0D0C07]">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      {error ? (
        <div className="max-w-5xl mx-auto px-6 pt-4">
          <div className="rounded-[12px] border-[0.5px] border-[#F5C2C7] bg-[#FFF5F5] px-4 py-3 text-[13px] text-[#8A1C1C]">
            {error}
          </div>
        </div>
      ) : null}
      <GroceriesPage
        productList={sharedData?.productList || []}
        maxStorageTime={sharedData?.maxStorageTime}
        leftovers={sharedData?.leftovers || []}
        checkedItemsOverride={sharedData?.shoppingModeState || {}}
        onCheckedItemsChange={handleCheckedItemsChange}
        allowShare={false}
        myProductsOverride={sharedData?.myProducts || []}
      />
    </>
  );
};

export default SharedGroceriesPage;